import React, { useState, useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../Lists.module.css';
import QueueView from '../listengine/QueueEngine';
import { ListCommand } from '../../../types/ListCommands';

const QUEUE_CODE = `class Queue {
  constructor() {
    this.items = [];
  }

  enqueue(element) {
    this.items.push(element);
  }

  dequeue() {
    if (this.isEmpty())
      return "Underflow";
    return this.items.shift();
  }

  isEmpty() {
    return this.items.length === 0;
  }
}`;

const QueueVisualizer: React.FC = () => {
  const navigate = useNavigate();
  const [command, setCommand] = useState<ListCommand | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeLine, setActiveLine] = useState<number | null>(null);

const animationResolver: React.MutableRefObject<((value?: void) => void) | null> = useRef(null);

  const handleEnqueue = () => {
    if (isAnimating || inputValue === '') return;
    setIsAnimating(true);
    setActiveLine(6); 
    setCommand({
      id: Date.now(),
      action: 'addLast', 
      value: parseInt(inputValue),
    });
    setInputValue('');
  };

  const handleDequeue = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveLine(12); 
    setCommand({ id: Date.now(), action: 'removeFirst' }); 
  };
  
  const handleAnimationComplete = () => {
    if (animationResolver.current) {
      animationResolver.current(); 
      animationResolver.current = null;
    } else {
      setIsAnimating(false); 
    }
    setActiveLine(null);
  };

  const handleReset = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCommand({ id: Date.now(), action: 'reset' });
  };

  const handleRandomAction = async () => {
    if (isAnimating) return;
    setIsAnimating(true);

    const isAdding = Math.random() < 0.5; 
    const count = Math.floor(Math.random() * 3) + 2; 

    for (let i = 0; i < count; i++) {
      await new Promise(resolve => {
        animationResolver.current = resolve;        
        if (isAdding) {
          setActiveLine(6); 
          const randomValue = Math.floor(Math.random() * 100);
          setCommand({ id: Date.now(), action: 'addLast', value: randomValue });
        } else {
          setActiveLine(12);
          setCommand({ id: Date.now(), action: 'removeFirst' });
        }
      });
    }  
    setIsAnimating(false);
  };

  return (
    <div className={styles.container}>
     <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
     </button>
      <h1 className={styles.title}>Queue(First-In-First-Out)</h1>
      <div className={styles.description}>
           <h2>Description</h2>
             <p>
               Imagine a line at a coffee shop. The first person to get in line is the first person to be served. This follows a <strong>First-In, First-Out (FIFO)</strong> principle. The first item added is the first one to be removed. Adding an item is called <code>enqueue</code>, and removing one is called <code>dequeue</code>.
             </p>
           </div>

           <div className={styles.useCase}>
             <h2>Use Cases</h2>
             <p>
               Queues are essential for managing tasks in order. A printer queue, tasks for a CPU, or handling requests on a web server all use queues. They are also fundamental in algorithms like Breadth-First Search (BFS) for traversing tree or graph data structures.
             </p>
           </div>

           <div className={styles.timeComplexity}>
             <h2>Time Complexity</h2>
             <p>
               Like a stack, a Queue's main operations are very efficient. Adding to the back (enqueue) and removing from the front (dequeue) are both <strong>O(1)</strong>, or constant time. Just like a stack, searching for or accessing a specific element requires checking elements from the front, resulting in <strong>O(n)</strong> time.
             </p>
       </div>
      <div className={styles.mainContent}>
        <div style={{flex: 1}}>
            <QueueView command={command} onComplete={handleAnimationComplete} />
        </div>
        <div className={styles.controlsAndCode} style={{flex: 1}}>
            <div className={styles.controls}>
                <input
                  type="number"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Value"
                  disabled={isAnimating}
                />
                <button onClick={handleEnqueue} disabled={isAnimating}>Add Last</button>
                <button onClick={handleDequeue} disabled={isAnimating}>Remove First</button>
                <button onClick={handleRandomAction} disabled={isAnimating}>Random</button>
                <button onClick={handleReset} disabled={isAnimating}>Reset</button>
            </div>
            <div className={styles.codeDisplay}>
            {QUEUE_CODE.split('\n').map((line, index) => (
              <div 
                key={index} 
                className={`${styles.codeLine} ${activeLine === index ? styles.highlightedCode : ''}`}
            >
                {line || ' '}
             </div>
             ))}
             </div>
        </div>
      </div>
    </div>
  );
};

export default QueueVisualizer;